import { authToken, setSessionRefresher } from '@/lib/api';

import { authApi } from './api/auth.api';
import type { LoginResponse } from './types';

/**
 * Rafraîchissement de session, à **un seul appel en vol**.
 *
 * ── Le problème que ce verrou règle ──────────────────────────────────────
 * Le serveur fait tourner le refresh token à chaque échange, et révoque
 * **toutes** les sessions du compte si un jeton déjà consommé se représente —
 * c'est sa détection de vol, et elle est juste. Mais deux échanges lancés en
 * même temps portent forcément le même cookie : le premier fait tourner le
 * jeton, le second arrive avec l'ancien, et le compte est intégralement
 * déconnecté. Sans que personne n'ait rien volé.
 *
 * Deux situations le déclenchaient :
 *
 *  - `<StrictMode>` monte deux fois le fournisseur d'authentification en
 *    développement, donc part deux fois ;
 *  - une salve de requêtes qui expirent ensemble demanderait autant de
 *    rafraîchissements qu'il y a d'appels en cours.
 *
 * La trace en base était sans ambiguïté : des rotations à une milliseconde
 * d'intervalle, et douze révocations en masse — dont une de trente-neuf jetons
 * d'un coup.
 *
 * ── Ce que le verrou ne couvre pas ───────────────────────────────────────
 * Deux **onglets** ouverts simultanément. Chacun a sa propre mémoire, donc son
 * propre verrou, et rien ici ne les coordonne. Seule une tolérance de rotation
 * côté serveur — accepter pendant quelques secondes un jeton qui vient d'être
 * remplacé, en renvoyant son successeur — règle ce cas.
 */

type Listener = (session: LoginResponse | null) => void;

const listeners = new Set<Listener>();

/**
 * Notifié à chaque changement de session, quelle qu'en soit l'origine.
 *
 * Indispensable depuis que le rafraîchissement peut partir d'un 401 au fond
 * d'un écran : le fournisseur d'authentification n'est plus le seul à le
 * déclencher, il doit donc l'apprendre plutôt que le provoquer.
 */
export const onSessionChange = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const notify = (session: LoginResponse | null): void => {
  listeners.forEach((listener) => {
    listener(session);
  });
};

/** Échange en cours, partagé par tous les appelants simultanés. */
let inFlight: Promise<LoginResponse> | null = null;

/**
 * Échange le cookie de refresh contre un nouveau token d'accès.
 *
 * Les appels concurrents reçoivent **la même promesse** : un seul échange part
 * sur le réseau, donc une seule rotation de jeton.
 */
export const refreshSession = (): Promise<LoginResponse> => {
  inFlight ??= authApi
    .refresh()
    .then((session) => {
      authToken.set(session.accessToken);
      notify(session);
      return session;
    })
    .catch((error: unknown) => {
      /* Le token est vidé avant de propager : c'est lui que le client HTTP
         interroge pour décider s'il tente un rafraîchissement. Vidé, il arrête
         net les tentatives suivantes sur une session close. */
      authToken.clear();
      notify(null);
      throw error;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
};

/* Branchement au client HTTP, au chargement du module.
   Effet de module assumé : c'est un singleton applicatif, au même titre que le
   stockage du token. Le faire dans un effet React le lierait au cycle de vie
   d'un composant, alors qu'un 401 peut survenir à tout instant. */
setSessionRefresher(refreshSession);
